import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FileText, Trash2, Loader2, ArrowRight, Layers } from "lucide-react";
import api, { formatApiError } from "@/api";
import Header from "@/components/Header";

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600" },
  uploaded: { label: "Drawing Ready", cls: "bg-amber-100 text-amber-700" },
  analyzed: { label: "Takeoff Ready", cls: "bg-green-100 text-green-700" },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  };
  useEffect(load, []);

  const createProject = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    setError("");
    try {
      const { data } = await api.post("/projects", { name: newName.trim() });
      navigate(`/projects/${data.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setCreating(false);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    setProjects((p) => p.filter((x) => x.id !== id));
  };

  return (
    <div className="App">
      <Header />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-200 pb-6">
          <div>
            <p className="uppercase text-xs font-bold tracking-[0.2em] text-slate-500">Workspace</p>
            <h1 className="font-display text-4xl font-black tracking-tighter text-slate-950 mt-1">
              Your Takeoffs
            </h1>
          </div>
          <button
            data-testid="new-project-btn"
            onClick={() => setShowForm((s) => !s)}
            className="flex items-center gap-2 bg-[#002FA7] text-white font-semibold px-5 py-3 hover:bg-[#002080] transition-colors"
          >
            <Plus size={18} /> New Takeoff
          </button>
        </div>

        {showForm && (
          <form
            onSubmit={createProject}
            data-testid="new-project-form"
            className="mt-6 bg-slate-50 border border-slate-200 p-5 flex flex-col sm:flex-row gap-3 sm:items-center"
          >
            <input
              autoFocus
              data-testid="project-name-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Project name (e.g. Building A — Level 2 Warehouse)"
              className="flex-1 border border-slate-300 px-4 py-3 rounded-sm outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]"
            />
            <button
              data-testid="create-project-submit-btn"
              type="submit"
              disabled={creating}
              className="bg-[#002FA7] text-white font-semibold px-6 py-3 hover:bg-[#002080] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {creating && <Loader2 size={16} className="animate-spin" />} Create
            </button>
          </form>
        )}
        {error && <p className="mt-3 text-sm text-[#DC2626]">{error}</p>}

        {/* Body */}
        <div className="mt-8">
          {projects === null ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-40 bg-slate-100 animate-pulse border border-slate-200" />
              ))}
            </div>
          ) : projects.length === 0 ? (
            <EmptyState onCreate={() => setShowForm(true)} />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="projects-grid">
              {projects.map((p) => {
                const meta = STATUS_META[p.status] || STATUS_META.draft;
                return (
                  <button
                    key={p.id}
                    data-testid={`project-card-${p.id}`}
                    onClick={() => navigate(`/projects/${p.id}`)}
                    className="text-left bg-white border border-slate-200 p-5 hover:border-slate-900 transition-colors group flex flex-col"
                  >
                    <div className="flex items-start justify-between">
                      <span className="grid place-items-center w-10 h-10 bg-slate-100 text-slate-700 group-hover:bg-[#002FA7] group-hover:text-white transition-colors">
                        <FileText size={20} />
                      </span>
                      <span
                        onClick={(e) => remove(p.id, e)}
                        data-testid={`delete-project-${p.id}`}
                        className="text-slate-300 hover:text-[#DC2626] transition-colors p-1"
                      >
                        <Trash2 size={16} />
                      </span>
                    </div>
                    <h3 className="font-display font-bold text-lg tracking-tight text-slate-900 mt-4 line-clamp-2">
                      {p.name}
                    </h3>
                    <div className="mt-auto pt-4 flex items-center justify-between">
                      <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 ${meta.cls}`}>
                        {meta.label}
                      </span>
                      <ArrowRight size={18} className="text-slate-400 group-hover:text-[#002FA7] group-hover:translate-x-1 transition-all" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div
      data-testid="empty-state"
      className="border-2 border-dashed border-slate-300 bg-slate-50 p-12 text-center flex flex-col items-center"
    >
      <span className="grid place-items-center w-14 h-14 bg-white border border-slate-200 text-[#002FA7]">
        <Layers size={26} />
      </span>
      <h3 className="font-display text-2xl font-black tracking-tight text-slate-900 mt-5">
        No takeoffs yet
      </h3>
      <p className="text-slate-600 mt-2 max-w-sm">
        Create a project, upload a fire sprinkler drawing, and generate your first
        material takeoff in seconds.
      </p>
      <button
        data-testid="empty-create-btn"
        onClick={onCreate}
        className="mt-6 flex items-center gap-2 bg-[#002FA7] text-white font-semibold px-6 py-3 hover:bg-[#002080] transition-colors"
      >
        <Plus size={18} /> Create your first takeoff
      </button>
    </div>
  );
}
